
import { Card, CardContent } from "@/components/ui/card";
import { DataOneUtils } from "./utils";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle, Info } from "lucide-react";

interface VehicleOverviewProps {
  data?: any;
  selectedStyle?: any;
  selectedStyleIndex?: number;
}

export const VehicleOverview = ({ data, selectedStyle, selectedStyleIndex = 0 }: VehicleOverviewProps) => {
  if (!data) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>No vehicle data</AlertTitle>
        <AlertDescription>
          Vehicle data is missing or invalid
        </AlertDescription>
      </Alert>
    );
  }

  // Extract vehicle info from selected style or overall data
  let vehicleInfo = null;
  
  if (selectedStyle) {
    // Try to extract info directly from the selected style
    vehicleInfo = {
      year: selectedStyle.year || selectedStyle.basic_data?.year || "Unknown",
      make: selectedStyle.make || selectedStyle.division || selectedStyle.basic_data?.make || "Unknown",
      model: selectedStyle.model || selectedStyle.basic_data?.model || "Unknown",
      trim: selectedStyle.trim || selectedStyle.name || selectedStyle.basic_data?.trim || "N/A",
      bodyStyle: selectedStyle.body_type || selectedStyle.body_style || selectedStyle.basic_data?.body_type || "N/A",
      driveType: selectedStyle.drive_type || selectedStyle.basic_data?.drive_type || "N/A",
      fuelType: selectedStyle.fuel_type || selectedStyle.basic_data?.fuel_type || "N/A",
      msrp: selectedStyle.msrp ? `$${selectedStyle.msrp}` : (selectedStyle.basic_data?.msrp ? `$${selectedStyle.basic_data.msrp}` : undefined)
    };
  } else {
    // Fall back to utility function for getting vehicle info from overall data
    vehicleInfo = DataOneUtils.getVehicleInfo(data);
  }
  
  if (!vehicleInfo) {
    return (
      <Alert variant="warning">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Data structure issue</AlertTitle>
        <AlertDescription>
          Could not extract vehicle information from the provided data
        </AlertDescription>
      </Alert>
    );
  }

  // Get multiple styles info
  const multipleStylesInfo = DataOneUtils.getVehicleInfo(data);
  const hasMultipleStyles = multipleStylesInfo?.hasMultipleStyles;
  const styleCount = multipleStylesInfo?.styleCount;

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold text-blue-800">Vehicle Overview</h2>
      
      <Card>
        <CardContent className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <h3 className="text-sm font-medium text-gray-500">Year</h3>
              <div className="text-lg font-medium">{vehicleInfo.year}</div>
            </div>
            
            <div>
              <h3 className="text-sm font-medium text-gray-500">Make</h3>
              <div className="text-lg font-medium">{vehicleInfo.make}</div>
            </div>
            
            <div>
              <h3 className="text-sm font-medium text-gray-500">Model</h3>
              <div className="text-lg font-medium">{vehicleInfo.model}</div>
            </div>
            
            <div>
              <h3 className="text-sm font-medium text-gray-500">Trim</h3>
              <div className="text-lg font-medium">{vehicleInfo.trim || 'N/A'}</div>
            </div>
          </div>
          
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <h3 className="text-sm font-medium text-gray-500">Body Style</h3>
              <div className="text-base">{vehicleInfo.bodyStyle || 'N/A'}</div>
            </div>
            
            <div>
              <h3 className="text-sm font-medium text-gray-500">Drive Type</h3>
              <div className="text-base">{vehicleInfo.driveType || 'N/A'}</div>
            </div>
            
            <div>
              <h3 className="text-sm font-medium text-gray-500">Fuel Type</h3>
              <div className="text-base">{vehicleInfo.fuelType || 'N/A'}</div>
            </div>
          </div>
          
          {vehicleInfo.msrp && (
            <div className="mt-4">
              <h3 className="text-sm font-medium text-gray-500">MSRP</h3>
              <div className="text-base">{vehicleInfo.msrp}</div>
            </div>
          )}
          
          {hasMultipleStyles && (
            <Alert className="mt-4 bg-blue-50 border-blue-200">
              <Info className="h-4 w-4" />
              <AlertTitle>Multiple Styles Available</AlertTitle>
              <AlertDescription>
                This vehicle has {styleCount} different style options. 
                {selectedStyle ? ` Currently viewing style ${selectedStyleIndex + 1}.` : ' Some information shown may be specific to the selected style.'}
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VehicleOverview;
